import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit,} from "@angular/core";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import {Subject} from "rxjs";
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../../config/hotel-tours-list-page.config';
import { SearchService } from '../../../services/search/search.service';
import { SmartFilterService } from '../../../services/smart-filter.service';
import {WindowRefService} from "../../../services/window-ref.service";
import {takeUntil} from "rxjs/operators";
import {ToursStateService} from "../../../services/tours-state.service";
import {HTTPSearchResultFiltersResponse, HTTPSort} from "../../../models";
import {EventBusService} from "../../../services/event-bus.service";

@Component({
  selector: 'app-smart-filters',
  templateUrl: './smart-filters.component.html',
  styleUrls: ['./smart-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SmartFiltersComponent extends OnDestroy$ implements OnInit, OnDestroy {
  @Input() place: string;
  @Input() displayMode: 'banner' | 'button' = 'banner';

  private destroy$ = new Subject<void>();

  isAllowSmartFilters: boolean = false;

  isUseSmartFilters: boolean;

  isLoadedFilters: boolean = false;

  abTestVariant: string | null = null;

  isOnlySmart: boolean = false;

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
    private windowService: WindowRefService,
    private smartFilterService: SmartFilterService,
    private searchService: SearchService,
    private cdRef: ChangeDetectorRef,
    private stateService: ToursStateService,
    private eventService: EventBusService,
  ) {
    super();

    this.isAllowSmartFilters = this.appConfig.isAllowSmartFilters;
    this.isOnlySmart = this.stateService.getState().applyedFilters.onlySmart;
  }

  ngOnInit(): void {
    this.allow();
    this.setAbTestVariant();

    if (this.place === 'modal') {
      this.isLoadedFilters = true;
      this.cdRef.detectChanges();
    } else {
      this.eventService.isApplySmartFiltersSub$
        .pipe(takeUntil(this.destroy$))
        .subscribe((p) => {
          if (p) {
            this.applySmartFilter();
            this.cdRef.detectChanges();
          }
        });
    }

    this.searchService.filters$.subscribe((response: HTTPSearchResultFiltersResponse) => {
      if (response?.total || response?.filters?.length || response?.prices) {
        this.isLoadedFilters = true;
        this.cdRef.detectChanges();
      }
    });

    this.searchService.applyFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const state = this.stateService.getState();
        if (state.selectedFilters.onlySmart === true) {
          this.isUseSmartFilters = true;
        }

        if (this.abTestVariant === 'toggle') {
          this.isOnlySmart = this.stateService.getState().applyedFilters.onlySmart
        }

        this.cdRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getSearchDescriptions(): { toCountry?: string; countryName?: string } {
    return this.windowService.nativeWindow.searchFormUtils?.getDescriptions?.() || {};
  }

  get toCountry() {
    return this.getSearchDescriptions().toCountry || '';
  }

  applySmartFilter() {
    this.smartFilterService.apply(this.filterList());
    this.searchService.applyFilters();

    const expires = new Date();
    expires.setDate(expires.getDate() + 365);
    // при необходимости добавьте ;SameSite=Lax;Secure
    document.cookie = `${encodeURIComponent('isUseSmartFilters')}=${encodeURIComponent(true)};expires=${expires.toUTCString()};path='/'`;

    this.isUseSmartFilters = true;

    this.cdRef.detectChanges();

    const modal = this.windowService.nativeWindow.modal;
    if (modal?.open) {
      modal.open({
        id: 'ng-modal-apply-smart-filters',
        type: modal.types?.FLOATING || 'floating',
        parentCssClass: 'smart-filters-apply-modal2',
        header: false,
        footer: false,
        content: {
          htmlDetachSelectorId: 'ng-modal-apply-smart-filters-content',
        },
        onBeforeClose: () => {
          if (this.place === 'modal') {
            // Закрываем основную модалку фильтров после подтверждения smart filters.
            setTimeout(() => {
              this.windowService.nativeWindow.modal?.close('#ng-modal-filters-modal2');
            }, 0);
          }
        },
      });
    }

    const eventProperties = {};
    eventProperties['Smart Filters Place'] = this.place;
    eventProperties['Country Name'] = this.getSearchDescriptions().countryName || '';

    this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('apply_smart_filters', eventProperties);
  }

  allow() {
    const match = document.cookie.match(new RegExp('(^|; )' + encodeURIComponent('isUseSmartFilters') + '=([^;]*)'));
    const raw = match ? decodeURIComponent(match[2]) : null;

    this.isUseSmartFilters = raw === 'true' || raw === '1';
  }

  filterList() {
    const countryId = this.searchService.getLastSearchParams()?.countryId;

    if (countryId) {
      switch (countryId) {
        /* Турция */
        case 1:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
              seaLines: [1],
              beachTypes: ['sandy'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [4, 3, 1]
              }
            },
          };
        /* Мальдивы */
        case 9:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
              seaLines: [1],
              beachTypes: ['sandy'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [4, 3, 67]
              }
            },
          };
        /* Вьетнам */
        case 7:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [3, 4, 7]
              }
            },
          };
        /* Египет */
        case 18:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
              seaLines: [1],
              beachTypes: ['sandy'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [4, 3, 1]
              }
            },
          };
        /* Тайланд */
        case 3:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [7],
              stars: ['3', '4', '5'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [3, 7, 4]
              }
            },
          };
        /* ОАЭ */
        case 2:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
              smartfilters: {
                bestHotelOnly: false,
                selectMode: 3,
                tagsIds: [3, 4, 7]
              }
            },
          };
        default:
          return {
            sort: HTTPSort.BY_PRICE,
            filters: {
              onlySmart: true,
              onlyRecommendedHotels: true,
              ratings: [8],
              stars: ['4', '5'],
            },
          };
      }
    }

    return null;
  }

  private lowerFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  public get descriptionFromFilters(): string {
    const list = this.filterList();
    if (!list) return '';

    const filters: any = list.filters;
    const sort: string = list.sort;

    if (!filters) return '';

    const parts: string[] = [];

    // seaLines (первая линия)
    if (Array.isArray(filters.seaLines) && filters.seaLines.includes(1)) {
      parts.push('первая линия пляжа');
    }

    // beachTypes
    if (Array.isArray(filters.beachTypes) && filters.beachTypes.length) {
      // маппим известные типы. Добавь свои варианты по необходимости.
      const mapBeachType = (bt: string) => {
        if (bt === 'sandy') return 'песчаный пляж';
        if (bt === 'pebble') return 'галечный пляж';
        return bt;
      };
      const btLabels = filters.beachTypes.map(mapBeachType);
      parts.push(btLabels.join(' / '));
    }

    // stars -> "4 и 5 звезды" или "4-звёздочные отели"
    if (Array.isArray(filters.stars) && filters.stars.length) {
      // сортируем числа по возрастанию и формируем фразу
      const starsSorted = [...filters.stars].map(String).sort();
      if (starsSorted.length === 1) {
        parts.push(`отели ${starsSorted[0]} звезды`);
      } else {
        parts.push(`отели ${starsSorted.join(',')} звезд`);
      }
    }

    // ratings -> "рейтинг отеля 8+"
    if (Array.isArray(filters.ratings) && filters.ratings.length) {
      // берём минимальное значение, например 8
      const minRating = Math.min(...filters.ratings);
      parts.push(`рейтинг отеля больше ${minRating}.0`);
    }

    // onlyRecommendedHotels
    if (filters.onlyRecommendedHotels) {
      parts.push('только проверенные отели от ht.kz');
    }

    // сортировка
    if (sort === HTTPSort.BY_PRICE) {
      parts.push('сортировка цены от дешёвых к дорогим');
    } else if (sort === HTTPSort.BY_SALES) {
      parts.push('сортировка цены по популярности');
    } else if (sort) {
      parts.push(`сортировка цены: ${String(sort)}`);
    }

    const tags = this.stateService.getTags();

    // smartfilters: если есть теги, можно указать кратко
    if (filters.smartfilters && Array.isArray(filters.smartfilters.tagsIds) && filters.smartfilters.tagsIds.length) {
      const tagIdsSet = new Set<number>(filters.smartfilters.tagsIds);
      const matchedNames = (tags || [])
        .filter(t => tagIdsSet.has(t.id))
        .map(t => this.lowerFirst(t.name));

      // убираем дубли, если вдруг
      const uniqueNames = Array.from(new Set(matchedNames));

      const ddd = uniqueNames.join(', ') || '—';
      parts.push(`туристы хвалят отель за: ${ddd}`);
    }

    if (!parts.length) return '';

    // Собираем финальную фразу
    // Пример: "Мы взяли во внимание фильтры: первая линия пляжа, сортировка от дешёвых к дорогим, отели 4 и 5 звезды, ht.kz рекомендует, рейтинг отеля 8+"
    return parts.join(', ');
  }

  setAbTestVariant() {
    const variant = this.windowService.nativeWindow.app.abTestList
      .find(s => s.startsWith("mobile-smart-filters-v3___"))?.split("___")[1] ?? null;

    if (variant) {
      this.abTestVariant = variant;
    }
  }

  toggle(event) {
    const currentTarget = event.currentTarget;

    if (currentTarget.checked) {
      this.applySmartFilter();
    } else {
      this.smartFilterService.apply({
        sort: HTTPSort.BY_SALES,
        filters: {
          onlySmart: false,
          onlyRecommendedHotels: false,
          ratings: [],
          stars: [],
          seaLines: [],
          beachTypes: [],
          smartfilters: {
            bestHotelOnly: false,
            selectMode: 3,
            tagsIds: []
          }
        },
      });
      this.searchService.applyFilters();
    }

    this.isOnlySmart = currentTarget.checked;
  }
}
